import type { components } from "./contract";
import { ApiError } from "./errors";

type Usage = components["schemas"]["V2UsageSummary"];
export type UsageSnapshot = Usage & { creditRevision?: number; creditSpace?: string };
type Spend = { revision: number; amount: number; pending: number };

/** Pending submission amounts stay separate from authoritative server balances. */
export class CreditBalance {
  private revision = 0;
  private listeners = new Set<() => void>();
  private spends = new Map<string, Map<string, Spend>>();
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  getRevision = () => this.revision;
  private changed() { this.revision++; this.listeners.forEach(listener => listener()); }

  async submit<T>(space: string, key: string, amount: number, request: () => Promise<T>): Promise<T> {
    let entries = this.spends.get(space);
    if (!entries) { entries = new Map(); this.spends.set(space, entries); }
    // Replays of the same command never subtract its quote twice.
    let spend = entries.get(key);
    if (!spend) { spend = { revision: this.revision + 1, amount: Math.max(0, amount), pending: 0 }; entries.set(key, spend); }
    spend.pending++;
    this.changed();
    try { return await request(); }
    catch (error) {
      // Network errors can occur after admission. Reconcile those with the
      // server; only a definite rejection can immediately undo the estimate.
      if (error instanceof ApiError && error.status >= 400 && error.status < 500 && ![408, 409, 429].includes(error.status)) spend.amount = 0;
      throw error;
    } finally { spend.pending--; this.changed(); }
  }

  hasPending(space: string) { return [...(this.spends.get(space)?.values() ?? [])].some(entry => entry.pending > 0); }

  async snapshot(space: string, read: () => Promise<Usage>): Promise<UsageSnapshot> {
    // A balance fetched during admission might already include a pending
    // command. Wait, then read once so the same credits cannot be deducted twice.
    for (;;) {
      if (this.hasPending(space)) {
        await new Promise<void>(resolve => { const unsubscribe = this.subscribe(() => { if (!this.hasPending(space)) { unsubscribe(); resolve(); } }); });
      }
      const revision = this.revision;
      const summary = await read();
      if (revision === this.revision) return { ...summary, creditRevision: revision, creditSpace: space };
    }
  }

  project(summary: UsageSnapshot): UsageSnapshot {
    const amount = [...(this.spends.get(summary.creditSpace ?? "")?.values() ?? [])]
      .filter(entry => entry.revision > (summary.creditRevision ?? 0))
      .reduce((total, entry) => total + entry.amount, 0);
    return amount === 0 ? summary : {
      ...summary,
      credits_available: summary.credits_available < 0 ? summary.credits_available : Math.max(0, summary.credits_available - amount),
      credits_reserved: summary.credits_reserved + amount,
    };
  }
}

export const creditBalance = new CreditBalance();
